// Nested data paths require keys as strings, so disabling for file.
/* eslint-disable quote-props */
/* eslint-disable no-shadow */
/* eslint-disable no-param-reassign */
import StoryService from '../../services/story';
import { generateSocialTemplates } from '../../utils/social';

const namespaced = true;
const state = () => ({
  tagboardId: null, // tagboard id Number
  posts: [],
  mutePreview: true,
  displayedPostIndex: 0,
  namespace: '', // the element id,
});

const getters = {
  getPostByPostId: (state) => (id) => state.posts.find((post) => post.post_id === id),
  getPostByIndex: (state) => (index) => state.posts[index],
  // eslint-disable-next-line max-len
  getPostsByPostType: (state) => (postType) => state.posts.find((post) => post.post_type === postType),
};

const actions = {
  updatePosts({ commit }, payload) {
    commit('updatePosts', payload);
  },
  updateTagboardId({ commit }, payload) {
    commit('updateTagboardId', payload);
  },
  updateMutePreview({ commit }, payload) {
    commit('updateMutePreview', payload);
  },
  setNamespace({ commit }, payload) {
    commit('SET_NAMESPACE', payload);
  },

  /**
   * Specifies the API data requirements.
   *
   * @typedef APIDataRequirements
   * @property {Object} params - Required parameters for the API call.
   * @property {number} params.tagboard_id - Identifier for the tagboard. Must be a positive
   *   integer.
   * @property {Object} [query] - Optional query parameters for the API call.
   * @property {number} [query.count] - Specifies the number of items to return. Value between
   *   1 and 100.
   * @property {boolean} [query.media_only] - Flag to indicate if only media items should be
   *   returned.
   * @property {boolean} [query.ids_only] - Flag to indicate if only the item IDs should be
   *    returned.
   * @property {string} [query.post_type] - The type of the post.
   * @property {string} [query.network] - The social network. Defaults to the valid networks list.
   * @property {string} [query.since] - Specifies the starting time or date for data retrieval.
   * @property {string} [query.before] - Specifies the ending time or date for data retrieval.
   * @property {('reverse_chron'|'chronological')} [query.order] - The order in which items should
   *   be returned. Defaults to 'reverse_chron'.
   */

  async fetchFeaturedPosts({ state, dispatch, commit }) {
    // todo: if tagboard's posts exist in some store get from store
    // else get from API
    const data = await StoryService.getFeaturedPosts(state.tagboardId);
    dispatch('updatePosts', data.posts || []);
    commit('updateDisplayedPostIndex', 0);
  },
  addDataElementAndMapping({ dispatch }, { element, parentId, namespace }) {
    const { key, type, settings } = element;
    dispatch(
      'graphicsTemplate/addNewElement',
      {
        value: {
          type,
          settings: { ...settings, socialModule: namespace, dataType: key },
          parentId,

          // Needed to avoid early group movement that causes elements to jump around
          skipGroupRecalculation: true,
        },
      },
      {
        root: true,
      },
    );
  },
  addDataElementsAndSetMappings({ dispatch }, {
    elements, parentId, namespace,
  }) {
    elements.reverse().forEach((element) => {
      dispatch('addDataElementAndMapping', { element, parentId, namespace });
    });
  },
  async createSocialTemplate({ dispatch }, { socialTemplateType, namespace, contentType }) {
    const templates = generateSocialTemplates({
      provider: namespace,
    });
    const template = templates[socialTemplateType];

    // from root state
    await dispatch('graphicsTemplate/addNewElement', {
      value: {
        type: 'social-data',
        isGroup: true,
        settings: {
          name: template.title,
          id: namespace,
        },
      },
    }, { root: true });

    template.contentOptions[contentType].layoutElements
      .reverse().forEach((el) => {
        const { type, ...rest } = el;
        dispatch('graphicsTemplate/addNewElement', {
          value: {
            type,
            isGroup: type === 'group', // todo, should have all group types
            parentId: namespace,
            settings: {
              type,
              ...rest,
            },
          },
        }, { root: true });
      });

    // create the mapped content for the social layouts
    dispatch('addDataElementsAndSetMappings', {
      elements: templates[socialTemplateType].contentOptions[contentType].dataElements,
      parentId: namespace,
      namespace,
    });
  },
};

const mutations = {
  updatePosts(state, payload) {
    state.posts = payload;
  },
  updateTagboardId(state, payload) {
    state.tagboardId = payload;
  },
  updateMutePreview(state, payload) {
    state.mutePreview = payload;
  },
  updateDisplayedPostIndex(state, payload) {
    state.displayedPostIndex = payload;
  },
  SET_NAMESPACE(state, payload) {
    state.namespace = payload;
  },
};

export default {
  namespaced,
  state,
  actions,
  getters,
  mutations,
};
